// DailyWiseReport.js
import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase/firebase-config";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { motion, AnimatePresence } from "framer-motion";
import "./All_Customers";


/* ---------- Helpers ---------- */
const toYMD = (d) => {
  if (!d) return "";
  const dt = typeof d === "string" ? new Date(d) : d;
  if (Number.isNaN(dt.getTime())) return "";
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const da = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${da}`;
};
const toNum = (v) => {
  const s = String(v ?? "").replace(/,/g, "");
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};
const fmt2 = (n) => toNum(n).toFixed(2);

/* ---------- Small Animated Bits (same style) ---------- */
const Sparkles = () => {
  const dots = React.useMemo(
    () =>
      Array.from({ length: 18 }).map((_, i) => ({
        id: i,
        left: `${Math.random() * 92 + 4}%`,
        top: `${Math.random() * 60 + 4}%`,
        size: Math.random() * 6 + 3,
        dur: Math.random() * 4 + 3,
        delay: Math.random() * 2,
      })),
    []
  );
  return (
    <div aria-hidden style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
      {dots.map((d) => (
        <motion.span
          key={d.id}
          initial={{ y: 16, opacity: 0 }}
          animate={{ y: [-8, 8, -8], opacity: [0.1, 0.28, 0.1] }}
          transition={{ duration: d.dur, repeat: Infinity, delay: d.delay, ease: "easeInOut" }}
          style={{
            position: "absolute",
            left: d.left,
            top: d.top,
            width: d.size,
            height: d.size,
            borderRadius: 999,
            background:
              "radial-gradient(circle at 30% 30%, rgba(255,255,255,.9), rgba(255,255,255,.0) 60%)",
            filter: "blur(.6px)",
          }}
        />
      ))}
    </div>
  );
};

const Confetti = ({ show = false }) => {
  const pieces = React.useMemo(
    () =>
      Array.from({ length: 26 }).map((_, i) => ({
        id: i,
        x: (Math.random() - 0.5) * 240,
        y: (Math.random() - 0.5) * 60,
        r: Math.random() * 360,
        s: Math.random() * 8 + 4,
        d: Math.random() * 0.6 + 0.6,
      })),
    []
  );
  if (!show) return null;
  return (
    <div style={{ position: "fixed", top: "15%", left: "50%", zIndex: 9999, pointerEvents: "none" }}>
      {pieces.map((p) => (
        <motion.div
          key={p.id}
          initial={{ x: 0, y: 0, rotate: 0, opacity: 1 }}
          animate={{ x: p.x, y: p.y + 160, rotate: p.r, opacity: [1, 1, 0] }}
          transition={{ duration: 1.3 * p.d, ease: "easeOut" }}
          style={{
            width: p.s,
            height: p.s,
            borderRadius: 2,
            background:
              ["#60A5FA", "#34D399", "#F472B6", "#FBBF24", "#A78BFA"][p.id % 5],
          }}
        />
      ))}
    </div>
  );
};

const Toast = ({ type = "success", text, onDone }) => {
  const colors =
    type === "success"
      ? ["#10B981", "#34D399"]
      : type === "update"
      ? ["#3B82F6", "#60A5FA"]
      : type === "delete"
      ? ["#EF4444", "#F87171"]
      : ["#6366F1", "#A78BFA"];
  const icon =
    type === "success" ? "✓" : type === "update" ? "✎" : type === "delete" ? "✖" : "ℹ";

  return (
    <motion.div
      initial={{ y: -30, opacity: 0, scale: 0.98 }}
      animate={{ y: 0, opacity: 1, scale: 1 }}
      exit={{ y: -30, opacity: 0, scale: 0.98 }}
      transition={{ type: "spring", stiffness: 260, damping: 22 }}
      onAnimationComplete={() => setTimeout(onDone, 1400)}
      style={{ position: "fixed", top: 18, left: "50%", transform: "translateX(-50%)", zIndex: 10000 }}
    >
      <div
        style={{
          padding: "10px 14px",
          borderRadius: 12,
          color: "white",
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          gap: 10,
          boxShadow: "0 10px 30px rgba(0,0,0,.15)",
          background: `linear-gradient(90deg, ${colors[0]}, ${colors[1]})`,
        }}
      >
        <span
          style={{
            width: 26,
            height: 26,
            display: "grid",
            placeItems: "center",
            background: "rgba(255,255,255,.25)",
            borderRadius: 8,
            fontSize: 14,
          }}
        >
          {icon}
        </span>
        <span>{text}</span>
      </div>
    </motion.div>
  );
};

/* ---------- Component ---------- */
const DailyWiseReport = () => {
  const colRef = collection(db, "daily-wise-report");

  const [rows, setRows] = useState([]); // {id, date, opening, profit, closing}
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [errors, setErrors] = useState({});
  const [search, setSearch] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // flair
  const [toast, setToast] = useState(null);
  const [confetti, setConfetti] = useState(false);
  const [rowGlowId, setRowGlowId] = useState(null);

  const [form, setForm] = useState({
    date: toYMD(new Date()),
    opening: "",
    profit: "",
    closing: "",
  });

  /* ---------- Load once ---------- */
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(colRef);
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setRows(list);
      } catch (e) {
        console.error("fetch error", e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------- Derived: sorted rows (no auto math now) ---------- */
  const sortedRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return [...rows]
      .sort((a, b) => (a.date > b.date ? 1 : a.date < b.date ? -1 : 0))
      .filter((r) =>
        !q
          ? true
          : [r.date, r.opening, r.profit, r.closing]
              .map((v) => String(v ?? "").toLowerCase())
              .some((s) => s.includes(q))
      );
  }, [rows, search]);

  const finalClosing = useMemo(
    () => (sortedRows.length ? toNum(sortedRows[sortedRows.length - 1].closing) : 0),
    [sortedRows]
  );

  /* ---------- Handlers ---------- */
  const fireToast = (type, text, confettiAlso = false) => {
    setToast({ type, text });
    if (confettiAlso) {
      setConfetti(true);
      setTimeout(() => setConfetti(false), 1000);
    }
  };

  const numericSan = (v) => v.replace(/[^\d.\-]/g, "");

  const onFormChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({
      ...p,
      [name]: ["opening", "profit", "closing"].includes(name) ? numericSan(value) : value,
    }));
  };

  const validate = () => {
    const e = {};
    if (!form.date) e.date = "Date is required";
    if (form.opening === "" || Number.isNaN(toNum(form.opening))) e.opening = "Enter a number";
    if (form.profit === "" || Number.isNaN(toNum(form.profit))) e.profit = "Enter a number";
    if (form.closing === "" || Number.isNaN(toNum(form.closing))) e.closing = "Enter a number";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const resetForm = () => {
    setEditId(null);
    setForm({ date: toYMD(new Date()), opening: "", profit: "", closing: "" });
    setErrors({});
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      fireToast("info", "Fix validation errors");
      return;
    }

    const payload = {
      date: form.date,
      opening: toNum(form.opening),
      profit: toNum(form.profit),
      closing: toNum(form.closing),
    };

    try {
      if (editId) {
        await updateDoc(doc(db, "daily-wise-report", editId), payload);
        setRows((p) => p.map((r) => (r.id === editId ? { ...r, ...payload } : r)));
        setRowGlowId(editId);
        fireToast("update", "Day updated", true);
      } else {
        const d = await addDoc(colRef, payload);
        setRows((p) => [...p, { id: d.id, ...payload }]);
        setRowGlowId(d.id);
        fireToast("success", "Day added", true);
      }
      setTimeout(() => setRowGlowId(null), 900);
      setShowForm(false);
      resetForm();
    } catch (err) {
      console.error(err);
      fireToast("delete", "Save failed");
    }
  };

  const onEdit = (row) => {
    setEditId(row.id);
    setForm({
      date: row.date,
      opening: String(row.opening ?? ""),
      profit: String(row.profit ?? ""),
      closing: String(row.closing ?? ""),
    });
    setShowForm(true);
    fireToast("info", "Edit mode");
  };

  const askDelete = (id) => {
    setConfirmDelete(id);
    fireToast("info", "Confirm delete?");
  };

  const confirmDeleteFn = async () => {
    const id = confirmDelete;
    if (!id) return;
    try {
      setDeletingId(id);
      await deleteDoc(doc(db, "daily-wise-report", id));
      setRows((p) => p.filter((r) => r.id !== id));
      fireToast("delete", "Record deleted", true);
    } catch (e) {
      console.error(e);
      fireToast("delete", "Delete failed");
    } finally {
      setDeletingId(null);
      setConfirmDelete(null);
    }
  };

  /* ---------- UI ---------- */
  return (
    <div className="boost-acc-wrapper" style={{ position: "relative" }}>
      <Sparkles />
      <Confetti show={confetti} />
      <AnimatePresence>
        {toast && <Toast key={toast.text} type={toast.type} text={toast.text} onDone={() => setToast(null)} />}
      </AnimatePresence>

      {/* Title */}
      <motion.h2
        className="title"
        initial={{ backgroundPositionX: "0%" }}
        animate={{ backgroundPositionX: ["0%", "100%", "0%"] }}
        transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
        style={{
          backgroundImage: "linear-gradient(90deg,#22d3ee,#a78bfa,#f472b6,#fbbf24,#22d3ee)",
          backgroundSize: "200% 100%",
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          color: "transparent",
          fontWeight: 800,
          letterSpacing: ".5px",
        }}
      >
        Daily Wise Report
      </motion.h2>

      {/* Command Bar */}
      <motion.div
        className="d-flex mb-3"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        style={{ gap: 12, alignItems: "center", flexWrap: "wrap" }}
      >
        <motion.button
          whileHover={{ scale: 1.03, y: -1 }}
          whileTap={{ scale: 0.98 }}
          className="btn"
          style={{
            background:
              "linear-gradient(90deg, rgba(59,130,246,.95), rgba(99,102,241,.95))",
            color: "white",
            border: "0",
            padding: "10px 16px",
            borderRadius: 12,
            boxShadow: "0 10px 20px -10px rgba(59,130,246,.55)",
            fontWeight: 700,
            letterSpacing: ".3px",
          }}
          onClick={() => {
            const opening = !showForm;
            if (opening) {
              resetForm();
              fireToast("info", "Add mode");
            } else {
              fireToast("info", "Form closed");
            }
            setShowForm(opening);
          }}
        >
          {showForm ? "Close" : "+ Add Day"}
        </motion.button>

        <div className="flex-grow-1" style={{ minWidth: 260, position: "relative" }}>
          <motion.input
            initial={{ filter: "blur(2px)" }}
            animate={{ filter: "blur(0px)" }}
            transition={{ duration: 0.35 }}
            type="text"
            className="form-control"
            placeholder="Search date / opening / profit / closing"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              paddingLeft: 42,
              height: 42,
              borderRadius: 12,
              border: "1px solid rgba(99,102,241,.25)",
              boxShadow: "inset 0 1px 3px rgba(0,0,0,.04)",
            }}
          />
          <span
            style={{
              position: "absolute",
              left: 12,
              top: "50%",
              transform: "translateY(-50%)",
              opacity: 0.65,
              fontSize: 18,
            }}
          >
            🔎
          </span>
        </div>
      </motion.div>

      {/* Modal Form */}
      <AnimatePresence>
        {showForm && (
          <motion.div className="modal show d-block" tabIndex="-1" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="modal-dialog">
              <motion.div
                className="modal-content form-animation"
                initial={{ scale: 0.95, y: 10, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.97, y: 10, opacity: 0 }}
                transition={{ type: "spring", stiffness: 240, damping: 20 }}
                style={{
                  borderRadius: 16,
                  overflow: "hidden",
                  boxShadow:
                    "0 12px 40px -12px rgba(99,102,241,.3), 0 8px 24px -16px rgba(236,72,153,.25)",
                  border: "1px solid rgba(99,102,241,.15)",
                }}
              >
                <div
                  className="modal-header"
                  style={{
                    background: "linear-gradient(90deg, rgba(59,130,246,.08), rgba(236,72,153,.08))",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <h5 className="modal-title" style={{ flex: 1 }}>
                    {editId ? "Edit Day" : "Add Day"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowForm(false);
                      fireToast("info", "Form closed");
                    }}
                  ></button>
                </div>

                <div className="modal-body">
                  <form onSubmit={onSubmit}>
                    <motion.div className="mb-3" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
                      <label>Date</label>
                      <input
                        type="date"
                        name="date"
                        className={`form-control ${errors.date ? "is-invalid" : ""}`}
                        value={form.date}
                        onChange={onFormChange}
                      />
                      {errors.date && <div className="text-danger small">{errors.date}</div>}
                    </motion.div>

                    <motion.div className="mb-3" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: 0.02 }}>
                      <label>Opening Balance</label>
                      <input
                        type="text"
                        name="opening"
                        inputMode="decimal"
                        placeholder="e.g., 5000"
                        className={`form-control ${errors.opening ? "is-invalid" : ""}`}
                        value={form.opening}
                        onChange={onFormChange}
                      />
                      {errors.opening && <div className="text-danger small">{errors.opening}</div>}
                    </motion.div>

                    <motion.div className="mb-3" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: 0.04 }}>
                      <label>Profit Amount</label>
                      <input
                        type="text"
                        name="profit"
                        inputMode="decimal"
                        placeholder="e.g., -1500 or 2500"
                        className={`form-control ${errors.profit ? "is-invalid" : ""}`}
                        value={form.profit}
                        onChange={onFormChange}
                      />
                      {errors.profit && <div className="text-danger small">{errors.profit}</div>}
                    </motion.div>

                    <motion.div className="mb-3" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: 0.06 }}>
                      <label>Closing Balance</label>
                      <input
                        type="text"
                        name="closing"
                        inputMode="decimal"
                        placeholder="e.g., 7500"
                        className={`form-control ${errors.closing ? "is-invalid" : ""}`}
                        value={form.closing}
                        onChange={onFormChange}
                      />
                      {errors.closing && <div className="text-danger small">{errors.closing}</div>}
                    </motion.div>

                    <motion.button
                      type="submit"
                      className="btn w-100"
                      whileHover={{ scale: 1.015, y: -1 }}
                      whileTap={{ scale: 0.985 }}
                      style={{
                        background: "linear-gradient(90deg, rgba(16,185,129,.95), rgba(5,150,105,.95))",
                        color: "white",
                        border: 0,
                        borderRadius: 12,
                        height: 44,
                        fontWeight: 700,
                        letterSpacing: ".3px",
                        boxShadow: "0 10px 22px -12px rgba(16,185,129,.55)",
                      }}
                    >
                      {editId ? "Update" : "Submit"}
                    </motion.button>
                  </form>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirm */}
      <AnimatePresence>
        {confirmDelete && (
          <motion.div className="modal show d-block" tabIndex="-1" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="modal-dialog">
              <motion.div
                className="modal-content"
                initial={{ scale: 0.95, y: 10, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.97, y: 10, opacity: 0 }}
                transition={{ type: "spring", stiffness: 260, damping: 22 }}
                style={{ borderRadius: 14 }}
              >
                <div className="modal-header">
                  <h5 className="modal-title">Delete record?</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setConfirmDelete(null);
                      fireToast("info", "Delete cancelled");
                    }}
                  ></button>
                </div>
                <div className="modal-body">This action cannot be undone.</div>
                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setConfirmDelete(null);
                      fireToast("info", "Delete cancelled");
                    }}
                  >
                    Cancel
                  </button>
                  <motion.button
                    className="btn btn-danger"
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={confirmDeleteFn}
                  >
                    Delete
                  </motion.button>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Table */}
      <div className="table-container mt-3">
        <div className="table-scroll">
          <table className="table table-striped custom-table">
            <thead>
              <tr>
                <th style={{ width: 70 }}>S.No</th>
                <th style={{ width: 140 }}>Date</th>
                <th style={{ width: 180 }}>Opening Balance</th>
                <th style={{ width: 140 }}>Profit</th>
                <th style={{ width: 180 }}>Closing Amount</th>
                <th style={{ width: 200 }}>Actions</th>
              </tr>
            </thead>
            <motion.tbody layout>
              <AnimatePresence initial={false}>
                {sortedRows.map((r, idx) => {
                  const glow = rowGlowId === r.id;
                  const neg = toNum(r.profit) < 0;
                  return (
                    <motion.tr
                      key={r.id}
                      layout
                      initial={{ opacity: 0, y: 8 }}
                      animate={{
                        opacity: 1,
                        y: 0,
                        boxShadow: glow ? "0 0 0 0 rgba(16,185,129,0)" : "none",
                      }}
                      exit={{ opacity: 0, y: -8, backgroundColor: "rgba(239,68,68,.08)" }}
                      transition={{ duration: 0.2 }}
                      onAnimationComplete={() => glow && setTimeout(() => setRowGlowId(null), 900)}
                      style={{
                        position: "relative",
                        background: glow ? "rgba(16,185,129,.07)" : "transparent",
                      }}
                      className={neg ? "row-neg" : "row-pos"}
                    >
                      {glow && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.98 }}
                          animate={{ opacity: [0.7, 0], scale: [1, 1.05] }}
                          transition={{ duration: 0.9 }}
                          style={{
                            position: "absolute",
                            inset: 0,
                            borderRadius: 10,
                            border: "2px solid rgba(16,185,129,.45)",
                            pointerEvents: "none",
                          }}
                        />
                      )}
                      <td>{idx + 1}</td>
                      <td>{r.date}</td>
                      <td className="fw-semibold">₹ {fmt2(r.opening)}</td>
                      <td className="fw-semibold">₹ {fmt2(r.profit)}</td>
                      <td className="fw-bold">₹ {fmt2(r.closing)}</td>
                      <td>
                        {deletingId === r.id ? (
                          <motion.span
                            className="text-danger fw-bold"
                            initial={{ opacity: 0.6 }}
                            animate={{ opacity: [0.6, 0.2, 0.6] }}
                            transition={{ repeat: Infinity, duration: 0.9 }}
                          >
                            Deleting…
                          </motion.span>
                        ) : (
                          <div className="d-flex" style={{ gap: 8 }}>
                            <motion.button
                              className="btn btn-sm btn-warning"
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.97 }}
                              onClick={() => onEdit(r)}
                            >
                              Edit
                            </motion.button>
                            <motion.button
                              className="btn btn-sm btn-danger"
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.97 }}
                              onClick={() => askDelete(r.id)}
                            >
                              Delete
                            </motion.button>
                          </div>
                        )}
                      </td>
                    </motion.tr>
                  );
                })}
                {sortedRows.length === 0 && (
                  <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 0.9 }} className="text-center">
                    <td colSpan={6} className="py-4">
                      No rows yet. Add your first day.
                    </td>
                  </motion.tr>
                )}
              </AnimatePresence>
            </motion.tbody>
            <tfoot>
              <tr>
                <td colSpan={4} className="text-end fw-bold">
                  Final Closing
                </td>
                <td colSpan={2} className={`fw-bold ${finalClosing < 0 ? "row-neg" : "row-pos"}`}>
                  ₹ {fmt2(finalClosing)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DailyWiseReport;
